import { differenceInDays } from 'date-fns';

import { MFAMethod } from '../../auth/types';
import { getImpersonationExpiresAt } from '../../utils/helpers/storage';

const canCallAnalytics = () => !getImpersonationExpiresAt();

export const analyticsIdentifyUser = (user: User) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.identify(user.slug, {
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        createdAt: user.activatedAt,
        account: user.accountName,
        userRole: user.roles[0],
        metro: user.metroAreaName,
      });
    window.analytics && window.analytics.group(user.slug, { account: user.accountName });
  }
};

export const analyticsPage = (pageName: string, properties?) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.page(pageName, {
        ...properties,
      });
  }
};

export const analyticsLogin = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Logged In');
  }
};

export const analyticsLogout = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Logged Out');
    window.analytics && window.analytics.reset();
  }
};

export const analyticsForgotPasswordInstructions = (email: string) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Sent Forgot Password Instructions', { email: email });
  }
};

export const analyticsCompleteRegistration = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Completed Registration');
  }
};

// Rides Table

export const analyticsClickViewCanceledRides = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Click Canceled Rides');
  }
};

export const analyticsViewAllCanceledChecked = (viewAllCancel: boolean) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Change Billed Cancel', { viewAllCancel });
  }
};

export const analyticsSearchedRides = (query: string, numResults: number) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Searched Rides', { query: query, numResults: numResults });
  }
};

// Rides Tracking

export const analyticsCreateRide = (slug: string, rideData: RideFormType, services: string[]) => {
  if (canCallAnalytics()) {
    const bookingWindow = differenceInDays(new Date(rideData.anchorTime || ''), new Date());
    const numRiders = [...new Set(rideData.waypointsAttributes.flatMap((waypoint) => waypoint.passengerIds) || [])];
    const numPrecisePinWaypoints = rideData.waypointsAttributes.filter(
      (waypoint) => waypoint.location?.preciseLocation
    );

    window.analytics &&
      window.analytics.track('Created Ride', {
        tripSlug: slug,
        anchor: rideData.timeAnchor === 'pick_up' ? 'Pickup' : 'Dropoff',
        bookingWindow: bookingWindow,
        numWaypoints: rideData.waypointsAttributes.length,
        numRiders: numRiders.length,
        numWaypointsMapPin: numPrecisePinWaypoints.length,
        ...(services.length > 0 && { hasService: services }),
      });
  }
};

export const analyticsCreateRideSeries = (templateSlug: string, rideData: RideFormType, services: string[]) => {
  if (canCallAnalytics()) {
    let daysOfWeek: string[] = [];

    for (const key in rideData.tripTemplateAttributes) {
      if (rideData.tripTemplateAttributes[key] === true) {
        daysOfWeek = [...daysOfWeek, key];
      }
    }
    const bookingWindow = differenceInDays(new Date(rideData.tripTemplateAttributes?.startDate || ''), new Date());
    const riders = [...new Set(rideData.waypointsAttributes.flatMap((waypoint) => waypoint.passengerIds) || [])];
    const numPrecisePinWaypoints = rideData.waypointsAttributes.filter(
      (waypoint) => waypoint.location?.preciseLocation
    );

    window.analytics &&
      window.analytics.track('Created Ride Series', {
        templateSlug: templateSlug,
        anchor: rideData.timeAnchor === 'pick_up' ? 'Pickup' : 'Dropoff',
        DOW: daysOfWeek,
        bookingWindow: bookingWindow,
        numWaypoints: rideData.waypointsAttributes.length,
        riderSlug: riders,
        numRiders: riders.length,
        numWaypointsMapPin: numPrecisePinWaypoints.length,
        ...(services.length > 0 && { hasService: services }),
      });
  }
};

export const analyticsEditedRide = (rideSlug: string, anchorTime: string, services: string[]) => {
  if (canCallAnalytics()) {
    const editWindow = differenceInDays(new Date(anchorTime || ''), new Date());

    window.analytics &&
      window.analytics.track('Edited Ride', {
        tripSlug: rideSlug,
        editWindow: editWindow,
        ...(services.length > 0 && { hasService: services }),
      });
  }
};

export const analyticsEditedRideSeries = (
  templateSlug: string,
  updatedRide: Partial<RideFormData> & { updateFuture?: string },
  wasSmartPoolingRec: boolean,
  services: string[],
  timeAnchor?: string
) => {
  if (canCallAnalytics() && updatedRide.tripTemplateAttributes) {
    let daysOfWeek: string[] = [];

    for (const key in updatedRide.tripTemplateAttributes) {
      if (updatedRide.tripTemplateAttributes[key] === true) {
        daysOfWeek = [...daysOfWeek, key];
      }
    }

    window.analytics &&
      window.analytics.track('Edited Ride Series', {
        templateSlug: templateSlug,
        whichRides: updatedRide?.updateFuture,
        anchor: timeAnchor === 'pick_up' ? 'Pickup' : 'Dropoff',
        wasSmartPoolingRec: wasSmartPoolingRec,
        ...(daysOfWeek.length && { DOW: daysOfWeek }),
        ...(updatedRide.tripTemplateAttributes?.startDate && {
          editWindow: differenceInDays(new Date(updatedRide.tripTemplateAttributes?.startDate || ''), new Date()),
        }),
        ...(updatedRide.waypointsAttributes && {
          numWaypoints: updatedRide.waypointsAttributes.length,
          numRiders: [...new Set(updatedRide.waypointsAttributes.flatMap((waypoint) => waypoint.passengerIds) || [])]
            .length,
          numWaypointsMapPin: updatedRide.waypointsAttributes.filter((waypoint) => waypoint.location?.preciseLocation)
            .length,
        }),
        ...(services.length && { hasService: services }),
      });
  }
};

export const analyticsRemoveRider = (
  riderSlug: string | undefined,
  rideSlug?: string | undefined,
  rideStatus?: string | undefined
) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Removed Rider', { tripSlug: rideSlug, riderSlug: riderSlug, tripState: rideStatus });
  }
};

export const analyticsCancelRide = (rideSlug: string, riderSlugs: string[], cancelWindow: number) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Canceled Ride', {
        tripSlug: rideSlug,
        riderSlug: riderSlugs,
        cancelWindow: cancelWindow,
      });
  }
};

export const analyticsCancelRideSeries = (
  templateSlug: string | undefined,
  riderSlugs: string[],
  whichRides: string,
  cancelWindow: number
) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Canceled Ride Series', {
        templateSlug: templateSlug,
        riderSlug: riderSlugs,
        whichRides: whichRides,
        cancelWindow: cancelWindow,
      });
  }
};

export const analyticsSelectedDeselectedAllRidesOnDay = (type: 'Selected' | 'Deselected', qty: number) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track(`${type} All Rides on a Day`, { qty });
  }
};

export const analyticsSelectedDeselectedSingleRide = (type: 'Selected' | 'Deselected') => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track(`${type} a Ride`);
  }
};

export const analyticsBulkCancel = (qty: number) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Clicked Bulk Cancel Button', { qty });
  }
};

export const analyticsShowTwilioNumber = (rideSlug: string | undefined, source: string) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Show CareDriver Twilio Number', { rideSlug: rideSlug, source: source });
  }
};

export const analyticsHitRideCap = (rideData: Partial<RideFormData>, estimatedFare: string) => {
  if (canCallAnalytics()) {
    let daysOfWeek: string[] = [];

    for (const key in rideData.tripTemplateAttributes) {
      if (rideData.tripTemplateAttributes[key] === true) {
        daysOfWeek = [...daysOfWeek, key];
      }
    }
    const riders = [...new Set(rideData.waypointsAttributes?.flatMap((waypoint) => waypoint.passengerIds) || [])];

    window.analytics &&
      window.analytics.track('Hit Ride Cap', {
        riderSlug: riders,
        estimatedFare: estimatedFare,
        DOW: daysOfWeek,
        seriesStartDate: rideData.tripTemplateAttributes?.startDate,
        seriesEndDate: rideData.tripTemplateAttributes?.endDate,
      });
  }
};

export const analyticsSubmittedRideRequest = (templateSlug: string, rideData: RideFormData, estimatedFare: string) => {
  if (canCallAnalytics()) {
    let daysOfWeek: string[] = [];

    for (const key in rideData.tripTemplateAttributes) {
      if (rideData.tripTemplateAttributes[key] === true) {
        daysOfWeek = [...daysOfWeek, key];
      }
    }
    const riders = [...new Set(rideData.waypointsAttributes?.flatMap((waypoint) => waypoint.passengerIds) || [])];

    window.analytics &&
      window.analytics.track('Submitted Ride Request', {
        riderSlug: riders,
        estimatedFare: estimatedFare,
        DOW: daysOfWeek,
        seriesStartDate: rideData.tripTemplateAttributes?.startDate,
        seriesEndDate: rideData.tripTemplateAttributes?.endDate,
        templateSlug: templateSlug,
      });
  }
};

export const analyticsCreateRideSeriesCancel = (user: User) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Create Ride Series Cancel', {
        orgEmail: user.email,
        orgFirstName: user.firstName,
        orgLastName: user.lastName,
      });
  }
};

// Rider Tracking

export const analyticsCreateRider = (riderData: {
  riderSlug?: string;
  category?: string;
  hasPhoto?: boolean;
  hasPhone?: boolean;
  numCareGivers?: number;
  numEContacts?: number;
  hasEmail?: boolean;
  errorValidation?: string[];
  isShared?: boolean;
  firstName?: string;
  lastName?: string;
}) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Created Rider', riderData);
  }
};

export const analyticsEditedRider = (riderSlug: string, riderData: RiderFormType, riderDetails) => {
  if (canCallAnalytics()) {
    const hasPhoto =
      !!riderData.photosAttributes[0]?.id ||
      (!!riderDetails.profilePhotoUrl && typeof riderData.photosAttributes[0]?.attachment === 'object');

    window.analytics &&
      window.analytics.track('Edited Rider', {
        riderSlug: riderSlug,
        category: riderData.category,
        hasPhoto: hasPhoto,
        hasPhone: !!riderData.mobilePhoneNumber,
        hasEmail: !!riderData.email,
        isShared: !!riderData.sharedBillingRider?.residenceAccountId,
      });
  }
};

export const analyticsEditRiderSafetySettings = (riderSlug: string, services: string[]) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Edited Rider Safety Settings', {
        riderSlug: riderSlug,
        ...(services.length > 0 && { hasService: services }),
      });
  }
};

export const analyticsCreateRiderStep = (stepNum: number) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Create Rider Wizard Progress', {
        stepNum,
      });
  }
};

// Rider Profile Form (IEP — RIQ-362)

export const analyticsRiderProfileFormOpened = (props: {
  action: 'create' | 'edit';
  entry_point: 'add_rider_button' | 'edit_rider_button' | 'contact_card' | 'rider_locations_card';
  sis_enabled: boolean;
}) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Profile Form Opened', props);
  }
};

export const analyticsRiderProfileSectionViewed = (props: { section_name: string; action: 'create' | 'edit' }) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Profile Section Viewed', props);
  }
};

export const analyticsRiderServicesFieldChanged = (props: {
  field_name: string;
  new_value: boolean;
  rider_id: string | null;
}) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Services Field Changed', props);
  }
};

export const analyticsRiderProfileSaveAttempted = (props: {
  action: 'create' | 'edit';
  has_validation_errors: boolean;
}) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Profile Save Attempted', props);
  }
};

export const analyticsRiderProfileSave = (props: {
  action: 'create' | 'edit';
  sections_visited: string[];
  structured_fields_set: string[];
  time_in_form_seconds: number;
  sis_import_used: boolean;
}) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Profile Save', props);
  }
};

export const analyticsRiderProfileAbandoned = (props: {
  action: 'create' | 'edit';
  last_active_section: string;
  time_in_form_seconds: number;
}) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Profile Abandoned', props);
  }
};

export const analyticsRiderProfileUnsavedChangesModalShown = (props: { action: 'create' | 'edit' }) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Profile Unsaved Changes Modal Shown', props);
  }
};

export const analyticsRiderProfileUnsavedChangesModalConfirmed = (props: { action: 'create' | 'edit' }) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Profile Unsaved Changes Modal Confirmed', props);
  }
};

export const analyticsRiderProfileUnsavedChangesModalDismissed = (props: { action: 'create' | 'edit' }) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Profile Unsaved Changes Modal Dismissed', props);
  }
};

export const analyticsRiderProfileFieldsEdited = (props: { action: 'create' | 'edit'; fields_edited: string[] }) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Rider Profile Fields Edited', props);
  }
};

export const analyticsClickAddRiderLocation = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Clicked Add Rider Location');
  }
};

export const analyticsAddRiderLocation = (isPrimary: boolean) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Added Rider Location', {
        isPrimary,
      });
  }
};

export const analyticsEditRiderLocation = (isPrimary: boolean) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Edited Rider Location', {
        isPrimary,
      });
  }
};

export const analyticsDeleteRiderLocation = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Deleted Rider Location');
  }
};

export const analyticsClickAddRiderContact = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Clicked Add Rider Contact');
  }
};

export const analyticsAddRiderContact = (contactDetails: RidersContact, contacts: RidersContact[]) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Added Rider Contact', {
        role: getRoleLabel(contactDetails.role),
        hasEmail: !!contactDetails.email,
        numCargegivers: contacts.reduce((count, contact) => (contact.role === 'caregiver' ? (count += 1) : count), 0),
        numEContacts: contacts.reduce(
          (count, contact) => (contact.role === 'emergency_contact' ? (count += 1) : count),
          0
        ),
      });
  }
};

export const analyticsViewRiderContact = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Viewed Rider Contact');
  }
};

export const analyticsEditRiderContact = (contactDetails: RidersContact) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Edited Rider Contact', {
        role: getRoleLabel(contactDetails.role),
        hasEmail: !!contactDetails.email,
      });
  }
};

export const analyticsDeleteRiderContact = (contactDetails: RidersContact) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Deleted Rider Contact', {
        role: getRoleLabel(contactDetails.role),
      });
  }
};

export const analyticsDeactivateRider = (riderSlug: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Deactivated Rider', {
        riderSlug,
      });
  }
};

export const analyticsReactivateRider = (riderSlug: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Reactivated Rider', {
        riderSlug,
      });
  }
};

export const analyticsClickUploadPhoto = (location: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Clicked Upload New Photo', {
        location,
      });
  }
};

export const analyticsEditedRiderPause = (riderData: { riderSlug: string; startDate?: Date; endDate?: Date }) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Edited Rider Pause', riderData);
  }
};

export const analyticsCanceledAllRides = (riderSlug: string) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Canceled All Rides', { riderSlug });
  }
};

export const analyticsAddNewLocation = (protocol: string, locationType: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Add New Location', {
        assignedMbmProtocol: protocol,
        assignedMbmProtocolLocationType: locationType,
      });
  }
};

export const analyticsEditedAssignedRiderLocation = (protocol: string, locationType: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Edited Assigned Rider Location', {
        assignedMbmProtocol: protocol,
        assignedMbmProtocolLocationType: locationType,
      });
  }
};

// Location Tracking

export const analyticsCreateLocation = (
  locationName: string,
  hasCalendar: boolean,
  hasMapPin: boolean,
  source?: string
) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Created Location', {
        locationName: locationName,
        hasCalendar: hasCalendar,
        hasMapPin: hasMapPin,
        source: source,
      });
  }
};

export const analyticsEditedLocation = (locationName: string, hasCalendar: boolean, hasMapPin: boolean) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Edited Location', {
        locationName: locationName,
        hasCalendar: hasCalendar,
        hasMapPin: hasMapPin,
      });
  }
};

export const analyticsEditedLocationRiderDetails = (locationName: string, riderSlug: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Edited Location Rider Details', {
        locationName: locationName,
        riderSlug: riderSlug,
      });
  }
};

// Calendars Tracking

export const analyticsCreateCalendar = (name: string) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Created Calendar', { calendarName: name });
  }
};

export const analyticsEditedCalendar = (name: string) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Edited Calendar', { calendarName: name });
  }
};

export const analyticsDeleteCalendar = (name: string) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Deleted Calendar', { calendarName: name });
  }
};

export const analyticsAddEditCalendarBreaks = (action: string, calendarName: string, type: string) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track(action, { calendarName: calendarName, type: type });
  }
};

export const analyticsRemovedCalendarBreaks = (calendarName: string, type: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Removed Calendar Day-Break', { calendarName: calendarName, type: type });
  }
};

export const analyticsAssignedCalendarLocation = (calendarName: string, locationName: string | undefined) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Assigned Calendar Location', { calendarName: calendarName, locationName: locationName });
  }
};

export const analyticsRemovedCalendarLocations = (calendarName: string, locationName: string | undefined) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Removed Calendar Location', { calendarName: calendarName, locationName: locationName });
  }
};

// Account Settings

export const analyticsEditedAccountSettings = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Edited Account Settings');
  }
};

export const analyticsInputAbsentAlertEmailAddress = (emails: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Input Absent Alert Email Address', { emailCount: emails?.trim().split(',').length });
  }
};

export const analyticsToggledAbsentEmailAlert = (sendAlert: string) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Toggled Absent Email Alert', { sendAlert: sendAlert === 'Yes' });
  }
};

export const analyticsEditCgRiderAlerts = (alertType: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Edit Cg Rider Alerts', {
        sendAlert: alertType === 'Yes',
        alertType: alertType === 'Yes' ? 'SMS' : 'None',
      });
  }
};

// Help Center

export const analyticsOpenedHelpCenter = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Opened Help Center');
  }
};

const getRoleLabel = (role: string) => (role === 'caregiver' ? 'Caregiver' : 'Emergency Contact');

// Smartpooling

export const analyticsSmartPoolingResults = (numRecommendations: number) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('SmartPooling Results Returned', { numRecommendations });
  }
};

export const analyticsClickedSmartpoolingRecommendations = (index: number, numRiders: number) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Clicked SmartPooling Recommendation', { index, numRiders });
  }
};

// Organizer Management

export const analyticsNewOrganizerCreated = ({ role, organizerSlug }: { role: string; organizerSlug: string }) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Created Organizer', { role, organizerSlug });
  }
};

export const analyticsDeactivatedOrganizer = (organizerSlug: string) => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Deactivated Organizer', { organizerSlug });
  }
};

export const analyticsResetOrganizerPassword = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Organizer Action Reset Password');
  }
};

export const analyticsResendOrganizerInvite = () => {
  if (canCallAnalytics()) {
    window.analytics && window.analytics.track('Organizer Action Resend Invite');
  }
};

// Dashboard MVP

export const analyticsOrganizerInvitedRider = (count: number, email: string[], assignedLocation) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Onsite Invited Dashboard', {
        count: count,
        email: email,
        role: 'Onsite Staff - View Only',
        assignedLocation: assignedLocation,
      });
  }
};

export const analyticsOrganizerSignup = (title: string, location?: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Onsite Signup Dashboard', {
        role: 'Onsite Staff - View Only',
        title: title,
        ...(location && { location: location }),
      });
  }
};

export const analyticsOrganizerViewedDashboard = (assignedLocation: string, rideCount: number, tab: string) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Viewed Dashboard', {
        assignedLocation: assignedLocation,
        rideCount: rideCount,
        tab: tab,
      });
  }
};

export const analyticsOrganizerViewedRideDetails = () => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Viewed Dashboard', {
        viewedRideDetails: true,
      });
  }
};

export const analyticsOrganizerPrintDashboardRides = (rideCount: number) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Print Dashboard Rides', {
        rideCount: rideCount,
      });
  }
};

// MFA

const mfaChannelProperties = (channel?: MFAMethod) =>
  channel === 'email' || channel === 'sms' ? { mfa_channel: channel } : {};

const trackMFAEvent = (event: string, properties: Record<string, unknown>) => {
  try {
    if (canCallAnalytics()) {
      window.analytics?.track(event, properties);
    }
  } catch {
    // Analytics availability must never interrupt authentication or cancellation.
  }
};

export const analyticsMFAMethodSelected = (channel: MFAMethod) => {
  trackMFAEvent('MFA Method Selected', mfaChannelProperties(channel));
};

export const analyticsMFAMethodOptionsShown = (origin: 'initial' | 'return') => {
  trackMFAEvent('MFA Method Options Shown', { mfa_choice_origin: origin });
};

export const analyticsMFAResend = (email: string, codePresent: boolean, disabled: boolean, channel?: MFAMethod) => {
  trackMFAEvent('Clicked Send Again MFA', {
    ...mfaChannelProperties(channel),
    email: email,
    codePresent: codePresent,
    disabled: disabled,
  });
};

export const analyticsMFAConfirmButton = (email: string, codePresent: boolean, channel?: MFAMethod) => {
  trackMFAEvent('Clicked Confirm MFA', {
    ...mfaChannelProperties(channel),
    email: email,
    codePresent: codePresent,
  });
};

export const analyticsMFACloseButton = (
  email: string,
  codePresent: boolean,
  closedFromError: boolean,
  channel?: MFAMethod
) => {
  trackMFAEvent('Closed MFA Modal', {
    ...mfaChannelProperties(channel),
    email: email,
    codePresent: codePresent,
    closedFromError: closedFromError,
  });
};

export const analyticsMFALearnMore = (email: string, codePresent: boolean) => {
  if (canCallAnalytics()) {
    window.analytics &&
      window.analytics.track('Clicked MFA Learn more', {
        email: email,
        codePresent: codePresent,
      });
  }
};

export const analyticsMFAError = (email: string, incorrectCode: boolean, expiredCode: boolean, channel?: MFAMethod) => {
  trackMFAEvent('MFA Error', {
    ...mfaChannelProperties(channel),
    email: email,
    incorrectCode: incorrectCode,
    expiredCode: expiredCode,
  });
};

interface BulkRiderCompletionProperties {
  outcome: 'success' | 'partial_failure' | 'failure';
  rider_count: number;
}

export const analyticsRidersBulkDeactivateCompleted = (properties: BulkRiderCompletionProperties) => {
  if (canCallAnalytics()) {
    window.analytics?.track('riders_bulk_deactivate_completed', {
      outcome: properties.outcome,
      rider_count: properties.rider_count,
    });
  }
};

export const analyticsRidersBulkReactivateCompleted = (properties: BulkRiderCompletionProperties) => {
  if (canCallAnalytics()) {
    window.analytics?.track('riders_bulk_reactivate_completed', {
      outcome: properties.outcome,
      rider_count: properties.rider_count,
    });
  }
};

export const analyticsRidersFilterApplied = (filters: Record<string, string[]>) => {
  if (canCallAnalytics()) {
    window.analytics?.track('riders_filter_applied', { filters });
  }
};

export const analyticsRidersFilterCleared = (previousFilters: Record<string, string[]>) => {
  if (canCallAnalytics()) {
    window.analytics?.track('riders_filter_cleared', { previous_filters: previousFilters });
  }
};

export const analyticsRidersSortApplied = (column: string, direction: string) => {
  if (canCallAnalytics()) {
    window.analytics?.track('riders_sort_applied', { column, direction });
  }
};

export const analyticsRidersSortCleared = (previousColumn: string, previousDirection: string) => {
  if (canCallAnalytics()) {
    window.analytics?.track('riders_sort_cleared', {
      previous_column: previousColumn,
      previous_direction: previousDirection,
    });
  }
};

export const analyticsRidersPageChanged = (pageNumber: number, totalPages: number, totalResults: number) => {
  if (canCallAnalytics()) {
    window.analytics?.track('riders_page_changed', {
      page_number: pageNumber,
      total_pages: totalPages,
      total_results: totalResults,
    });
  }
};

export const analyticsRidesFilterApplied = (filters: Record<string, string[]>) => {
  if (canCallAnalytics()) {
    window.analytics?.track('rides_filter_applied', { filters });
  }
};

export const analyticsRidesFilterCleared = (previousFilters: Record<string, string[]>) => {
  if (canCallAnalytics()) {
    window.analytics?.track('rides_filter_cleared', { previous_filters: previousFilters });
  }
};

export const analyticsRidesPageChanged = (pageNumber: number, totalPages: number, totalResults: number) => {
  if (canCallAnalytics()) {
    window.analytics?.track('rides_page_changed', {
      page_number: pageNumber,
      total_pages: totalPages,
      total_results: totalResults,
    });
  }
};

export const analyticsRiderSummaryCsvDownloaded = (user: User) => {
  if (canCallAnalytics()) {
    window.analytics?.track('rider_summary_csv_downloaded', {
      account_id: user.accountId,
      account_tier: user.tierName,
      user_id: user.id,
    });
  }
};

export const analyticsRiderTabViewed = (user: User) => {
  if (canCallAnalytics()) {
    window.analytics?.track('rider_tab_viewed', {
      account_id: user.accountId,
      account_tier: user.tierName,
      user_id: user.id,
    });
  }
};

export const analyticsRiderSummaryRunInitiated = (user: User) => {
  if (canCallAnalytics()) {
    window.analytics?.track('rider_summary_run_initiated', {
      account_id: user.accountId,
      account_tier: user.tierName,
      user_id: user.id,
    });
  }
};

export const analyticsRiderSummaryResultsViewed = (user: User) => {
  if (canCallAnalytics()) {
    window.analytics?.track('rider_summary_results_viewed', {
      account_id: user.accountId,
      account_tier: user.tierName,
      user_id: user.id,
    });
  }
};
